import {
  type AnchorHTMLAttributes,
  createContext,
  type HTMLAttributes,
  type ReactNode,
  useContext,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import { Link, uiStyles } from '../ui';
import './tokens.css';

export type ThemeMode = 'light' | 'dark';
// Vertical gaps between the heading block, the form, and the footer — shared by
// login and signup so their rhythm stays in lockstep.
export const SECTION_SPACING = 'var(--ap-section-gap)';

const TERMS_URL = 'https://www.reveliolabs.com/terms/';
const PRIVACY_URL = 'https://www.reveliolabs.com/privacy/';

// Marks "rendering inside the themed auth shell". password-input is the one
// consumer: it serves the themed field in the shell and Chakra's classic field
// on the un-themed pages — a structural distinction, not a colour, so no token
// can express it.
const ThemeModeContext = createContext<ThemeMode | null>(null);
export const ThemeModeProvider = ThemeModeContext.Provider;
export const useThemeMode = () => useContext(ThemeModeContext);

// `?theme=light|dark` pins a palette regardless of OS — for QA, support
// and stories that need one specific variant rather than "whatever this
// machine prefers".
export const THEME_PARAM = 'theme';

// Auth pages default to light; dark is opt-in via `?theme=dark`
// (QA / support / stories). `?s=website` also lands on light.
export function useResolvedThemeMode(): ThemeMode {
  const [searchParams] = useSearchParams();
  const pinnedMode = searchParams.get(THEME_PARAM);

  if (pinnedMode === 'light' || pinnedMode === 'dark') {
    return pinnedMode;
  }
  return 'light';
}

export function ThemeHeading({ children }: { children: ReactNode }) {
  // The auth typeface is set on the heading itself: the shell's inherited
  // font doesn't reach it.
  return <h1 className={uiStyles.heading}>{children}</h1>;
}

export function ThemeTagline({
  children,
  ...props
}: HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p {...props} className={uiStyles.tagline}>
      {children}
    </p>
  );
}

export function ThemeLink(props: AnchorHTMLAttributes<HTMLAnchorElement>) {
  return <Link {...props} />;
}

// `action` must name the button this footer sits under — the disclaimer binds
// the user to the terms by pressing it, so it can't name an action the form
// doesn't offer.
export function LegalFooter({ action }: { action: string }) {
  return (
    <p className={uiStyles.legalFooter}>
      By clicking “{action}” you agree to our{' '}
      <ThemeLink href={TERMS_URL} target="_blank" rel="noopener noreferrer">
        Terms of Service
      </ThemeLink>{' '}
      and acknowledge our{' '}
      <ThemeLink href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
        Privacy Policy
      </ThemeLink>{' '}
      and{' '}
      <ThemeLink href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
        Cookie Policy
      </ThemeLink>
      .
    </p>
  );
}
